import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Puzzle, Landmark, CheckCircle2, AlertTriangle, Trash2, Loader2, ExternalLink, Shield } from "lucide-react";

export default function AdminIntegrations() {
  return (
    <DashboardShell>
      <div className="p-8 max-w-5xl">
        <div className="mb-6">
          <div className="overline text-slate-500">Platform</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }} data-testid="admin-integrations-heading">
            <Puzzle className="w-7 h-7 text-slate-700" /> Integrations
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Platform-wide 3rd-party connections. Each investor still brings their own Stripe / PayPal / bank through Settings — these are the keys the platform itself uses.
          </p>
        </div>

        <PlaidIntegrationCard />
      </div>
    </DashboardShell>
  );
}

function PlaidIntegrationCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ plaid_client_id: "", plaid_env: "sandbox", plaid_secret_sandbox: "", plaid_secret_production: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations/plaid");
      setCfg(data);
      setForm({
        plaid_client_id: data.plaid_client_id || "",
        plaid_env: data.plaid_env || "sandbox",
        plaid_secret_sandbox: "",
        plaid_secret_production: "",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load Plaid config");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        plaid_client_id: form.plaid_client_id.trim() || undefined,
        plaid_env: form.plaid_env,
      };
      if (form.plaid_secret_sandbox.trim()) body.plaid_secret_sandbox = form.plaid_secret_sandbox.trim();
      if (form.plaid_secret_production.trim()) body.plaid_secret_production = form.plaid_secret_production.trim();
      await api.put("/admin/integrations/plaid", body);
      toast.success("Plaid credentials verified & saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear stored Plaid credentials? Investors will lose the Connect-bank option until new keys are added.")) return;
    try {
      await api.delete("/admin/integrations/plaid");
      toast.success("Plaid credentials cleared");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't clear");
    }
  };

  if (loading) return <div className="bg-white border border-slate-200 rounded-sm p-6 text-sm text-slate-400">Loading…</div>;

  const ready = cfg?.ready;
  const source = cfg?.source;
  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="plaid-integration-card">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-emerald-700 text-white flex items-center justify-center rounded-sm">
            <Landmark className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              Plaid (Bank verification)
              {ready ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm" data-testid="plaid-status-ready">
                  <CheckCircle2 className="w-3 h-3" /> Ready
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm" data-testid="plaid-status-missing">
                  <AlertTriangle className="w-3 h-3" /> Not configured
                </span>
              )}
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-sm">
                Source: {source || "none"}
              </span>
            </h2>
            <p className="text-sm text-slate-500">
              Powers the "Connect bank via Plaid" button on each investor's payment-gateways settings. Free to sign up at{" "}
              <a href="https://dashboard.plaid.com/signup" target="_blank" rel="noreferrer" className="underline hover:text-slate-900 inline-flex items-center gap-1">
                dashboard.plaid.com <ExternalLink className="w-3 h-3" />
              </a>.
            </p>
          </div>
        </div>
        {cfg?.plaid_client_id && (
          <button onClick={clear} className="text-slate-500 hover:text-red-600 p-1.5" title="Clear stored credentials" data-testid="plaid-clear-button">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="rounded-sm bg-slate-50 border border-slate-200 p-3 mb-5 text-xs text-slate-600 leading-relaxed flex items-start gap-2">
        <Shield className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <div>
          Secrets are stored server-side and never echoed back — the UI only shows a masked preview. Credentials saved here <b>override</b> anything set via <code className="font-mono bg-white border border-slate-200 px-1 rounded">PLAID_*</code> env vars in <code className="font-mono bg-white border border-slate-200 px-1 rounded">backend/.env</code>.
        </div>
      </div>

      <form onSubmit={save} className="space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Plaid client_id *">
            <input
              type="text"
              className="input-clean w-full font-mono text-xs"
              data-testid="plaid-client-id-input"
              placeholder="65••••••• from Plaid Team Settings → Keys"
              value={form.plaid_client_id}
              onChange={(e) => setForm({ ...form, plaid_client_id: e.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field label="Environment *">
            <select
              className="input-clean w-full"
              data-testid="plaid-env-select"
              value={form.plaid_env}
              onChange={(e) => setForm({ ...form, plaid_env: e.target.value })}
            >
              <option value="sandbox">Sandbox (development / testing)</option>
              <option value="production">Production (live bank connections)</option>
            </select>
          </Field>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Field label={`Sandbox secret ${cfg?.plaid_secret_sandbox_preview ? `(current: ${cfg.plaid_secret_sandbox_preview} — leave blank to keep)` : "*"}`}>
            <input
              type="password"
              className="input-clean w-full font-mono text-xs"
              data-testid="plaid-secret-sandbox-input"
              placeholder={cfg?.plaid_secret_sandbox_preview || "sandbox secret (required for env=sandbox)"}
              value={form.plaid_secret_sandbox}
              onChange={(e) => setForm({ ...form, plaid_secret_sandbox: e.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field label={`Production secret ${cfg?.plaid_secret_production_preview ? `(current: ${cfg.plaid_secret_production_preview} — leave blank to keep)` : "(optional)"}`}>
            <input
              type="password"
              className="input-clean w-full font-mono text-xs"
              data-testid="plaid-secret-production-input"
              placeholder={cfg?.plaid_secret_production_preview || "production secret (required for env=production)"}
              value={form.plaid_secret_production}
              onChange={(e) => setForm({ ...form, plaid_secret_production: e.target.value })}
              autoComplete="off"
            />
          </Field>
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-center gap-2">
          <button type="submit" disabled={saving} className="btn-primary-dark flex items-center gap-2" data-testid="plaid-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Verify & save
          </button>
          <span className="text-xs text-slate-500">Clicking Save hits Plaid's <code className="font-mono bg-slate-100 px-1 rounded">/link/token/create</code> to validate before persisting.</span>
        </div>
      </form>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
